import { ref, computed, watch, toRefs, nextTick } from 'vue';
import { fireAddToCart, fireMemberFavoriteAddToCart } from '@global/gtm/common';
import { btnDescriptionEnum, btnStyleEnum } from '@/models/productEnum';
import gtmTrace from '@global/gtm/trace';
import $ from 'jquery';

import useMiniCartStore from '@global/stores/useMiniCartStore';
import useUtmKDetect from '@global/composable/useUtmKDetect';
import useMsg from '@global/composable/useMessage';
import useAlert from '@global/composable/useAlert';
import useAPIHandler from '@global/composable/api/useAxiosAPI';
import useModalStore from '@global/stores/useModalStore';

const miniCartStore = useMiniCartStore();
const { getCartInfo } = miniCartStore;

const modalStore = useModalStore();
const { openModal } = modalStore;

const { utmKDetect } = useUtmKDetect();
const { messageBox } = useMsg();
const { alertDialog } = useAlert();
const { postShoppingCartAPI } = useAPIHandler();

function useCommonDialog(props, emit) {
  const { isOpen, allData, selectedID, dataLoad, listName } = toRefs(props.dialogData);

  const selectedSpec = ref({ Name: '尺寸', Key: 'default' });
  watch(selectedID, () => {
    selectedSpec.value = { Name: '尺寸', Key: 'default' };
    getSelectedData();
    checkIfOnlyOneHasStock();
  });

  const btnStyle = ref(btnStyleEnum.disabled);
  const btnStyleGenerate = computed({
    get: () => {
      if (allData.value.IsPreShow) {
        return 'disabled';
      }
      return btnStyle.value;
    },
    set: (newVal) => {
      btnStyle.value = newVal;
    },
  });
  const btnDescription = ref(btnDescriptionEnum.general);
  const btnDescriptionGenerate = computed({
    get: () => {
      if (allData.value.IsPreShow) {
        return 'COMING SOON';
      }
      return btnDescription.value;
    },
    set: (newValue) => {
      btnDescription.value = newValue;
    },
  });

  const mapSellPrice = computed(() => {
    return allData.value.EventPrice > 0 && allData.value.EventPrice < allData.value.SellPrice
      ? allData.value.EventPrice.toLocaleString()
      : allData.value.SellPrice.toLocaleString();
  });

  const selected = ref({});
  function getSelectedData() {
    allData.value.Colors?.forEach((element) => {
      if (element.ID === selectedID.value) {
        selected.value = element;
      }
    });
  }

  function checkIfOnlyOneHasStock() {
    let hasStockCount = 0;
    let hasStockIndex = 0;
    selected.value.Specs.forEach((element, index) => {
      if (parseInt(element.Stock) !== 0) {
        hasStockCount += 1;
        hasStockIndex = index;
      }
    });
    if (hasStockCount === 1) {
      selectSpec(selected.value.Specs[hasStockIndex]);
    }
  }

  const selectedSpecID = ref(null);
  const selectedSpecKey = ref(null);
  function selectSpec(spec) {
    if (spec.Key === 'default') {
      btnStyleGenerate.value = btnStyleEnum.disabled;
      btnDescriptionGenerate.value = btnDescriptionEnum.general;
      clearSpec();
      return;
    }
    selectedSpec.value = spec;
    selectedSpecID.value = spec.ID;
    selectedSpecKey.value = spec.Key;
    resetCounter();
    btnActive(spec.Stock, spec.IsOutOfStock, spec.PreOrder);
  }

  function btnActive(stock, isSoldOut, isPreOrder) {
    if (allData.value.IsPreShow) return;

    if (stock === 0 && isSoldOut && !isPreOrder) {
      // 現貨無庫存且斷貨
      btnStyleGenerate.value = btnStyleEnum.disabled;
      btnDescriptionGenerate.value = btnDescriptionEnum.soldOut;
    } else if (stock === 0) {
      // 無庫存
      btnStyleGenerate.value = btnStyleEnum.activeMail;
      btnDescriptionGenerate.value = btnDescriptionEnum.notice;
    } else if (isPreOrder && stock > 0) {
      btnStyleGenerate.value = btnStyleEnum.active;
      btnDescriptionGenerate.value = btnDescriptionEnum.addPre || btnDescriptionEnum.addBag;
    } else {
      btnStyleGenerate.value = btnStyleEnum.active;
      btnDescriptionGenerate.value = btnDescriptionEnum.addBag;
    }
  }

  /**
   * 數量計算
   */
  const counter = ref(1);
  function addCount(maxCount) {
    if (counter.value >= maxCount) return;
    counter.value += 1;
  }
  function reduceCount() {
    if (counter.value <= 1) return;
    counter.value -= 1;
  }
  function resetCounter() {
    counter.value = 1;
  }

  /**
   * 換顏色/規格
   */
  function changeImg(colorID) {
    if (selected.value.ID === colorID) return;
    btnReset();
    clearSpec();
    resetCounter();
    emit('changeSelect', colorID);
  }

  /**
   * 關閉跳窗
   */
  function closeSelf() {
    emit('changeOpen', false);
    btnReset();
    clearSpec();
  }
  function clearSpec() {
    selectedSpec.value = { Name: '尺寸', Key: 'default' };
    selectedSpecID.value = null;
    selectedSpecKey.value = null;
  }
  function btnReset() {
    btnStyleGenerate.value = 'disabled';
    btnDescriptionGenerate.value = btnDescriptionEnum.general;
  }

  /**
   * 新增購物車
   */
  const itemSpecInfo = ref('');
  function addToCart() {
    if (allData.value.IsPreShow) return;

    if (selectedSpec.value.Key === 'default') {
      alertDialog.alert('請選擇尺寸');
      return;
    }

    const marketID = allData.value.CustomMarketID?.substring(
      0,
      allData.value.CustomMarketID?.indexOf('CL'),
    );

    itemSpecInfo.value = `${marketID}CL${selectedID.value}SZ${selectedSpecKey.value}`;

    if (selectedSpec.value.Stock === 0) {
      // 庫存為0且為斷貨商品且不是預購商品
      if (selectedSpec.value.IsOutOfStock && !selectedSpec.value.PreOrder) return;

      // 有些品牌原本就沒有貨到通知
      if (props.noArrivalNotice) return;

      // 1.庫存為0且不是斷貨商品(不用管預購不預購)
      // 2.庫存為0且是斷貨商品但為預購商品
      openModal('ArrivalNotice', itemSpecInfo.value);
      return;
    }

    const utmData = utmKDetect(itemSpecInfo.value) === null ? null : utmKDetect(itemSpecInfo.value);

    postShoppingCartAPI({
      CustomMarketID: itemSpecInfo.value,
      Count: counter.value,
      UtmTag: utmData,
    })
      .then((res) => {
        if (res.data.Code === 200) {
          if (window.PictureContainer.includes('pazzo') || window.PictureContainer.includes('meierq')) {
            alertDialog.special('已加入購物車');
          } else {
            messageBox.cart('已加入購物車');
          }
          getCartInfo();

          gtmTrace.addToCart({
            name: allData.value.Name,
            id: itemSpecInfo.value,
            price: allData.value.OriginPrice,
            sale_price: mapSellPrice.value,
            img_url: selected.value.Images[0].Url,
            product_url: selected.value.MarketUrl,
            color: selected.value.Name,
            count: counter.value,
          });

          fireAddToCart({
            item_id: allData.value.CustomMarketID.split('SZ')[0],
            item_name: allData.value.Name,
            index: 0,
            affiliation: selected.value.Name,
            item_brand: selectedSpec.value.Name,
            item_list_name: listName.value,
            price: mapSellPrice.value,
            quantity: counter.value,
            item_variant: selectedSpec.value.Barcode,
          });

          if (listName.value === '會員收藏清單') {
            fireMemberFavoriteAddToCart({
              productId: allData.value.CustomMarketID.split('SZ')[0],
              productName: allData.value.Name,
              addFavoriteItem: {
                item_id: allData.value.CustomMarketID.split('SZ')[0],
                item_name: allData.value.Name,
                index: 0,
                affiliation: selected.value.Name,
                item_brand: selectedSpec.value.Name,
                item_list_name: listName.value,
                price: mapSellPrice.value,
                quantity: counter.value,
                item_variant: selectedSpec.value.Barcode,
              },
            });
          }

          closeSelf();
          if (listName.value === '購物車加價購') {
            window.location.reload();
          }
        } else if (res.data.Code === 205) {
          alertDialog.alert('商品已售完', () => {
            window.location.reload();
          });
        }
      })
      .catch((error) => {
        console.log(error);
      });
  }

  function showProductInfo(data) {
    openModal('AddonDetailPopup', data);
    nextTick(() => {
      $('.custom-modal-wrap').css('z-index', '2000');
      $('.base-modal').css('z-index', '2000');
    });
  }

  return {
    itemSpecInfo,
    isOpen,
    allData,
    dataLoad,
    mapSellPrice,
    btnStyleGenerate,
    btnDescriptionGenerate,
    selectedSpec,
    selected,
    selectedSpecID,
    selectedSpecKey,
    selectSpec,
    counter,
    addCount,
    reduceCount,
    changeImg,
    closeSelf,
    addToCart,
    showProductInfo,
  };
}

export default useCommonDialog;
